import React, { useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { Table, TableBody, TableContainer, TableHead, TableRow } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import UpIcon from '@material-ui/icons/KeyboardArrowUp';
import LimpiarFiltros from '../../assets/images/limpiar-filtros.svg';
import '../../assets/css/vec-datatables.css';
import Security from '../../commons/security/Security.js';
import * as Utils from '../../commons/utils/Utils';
import { StyledTableCellProps as StyledTableCell } from '../../commons/utils/TableStyles';
import { buildEmptyFilterState, serializeFilterState } from '../../commons/filters/GridFilterPanel.jsx';
import GridSearchResultsBadge from '../../commons/filters/GridSearchResultsBadge.jsx';
import GridPagination from '../../commons/grid/GridPagination.jsx';
import { GridToolbar, GridToolbarButton } from '../../commons/grid/GridToolbar.jsx';
import { GridEmptyRow, GridLoadingRow } from '../../commons/grid/GridStateRow.jsx';
import SortableCell from '../../commons/grid/SortableCell.jsx';
import GridColumnSelector from '../../commons/grid/GridColumnSelector.jsx';
import useColumnVisibility from '../../commons/grid/useColumnVisibility.js';
import useGridState from '../../commons/grid/useGridState.js';
import EquipamientoGridExport from './EquipamientoGridExport.jsx';
import EquipamientoGridFiltros from './EquipamientoGridFiltros.jsx';
import EquipamientoGridRow from './EquipamientoGridRow.jsx';
import { EQUIPAMIENTO_COLUMNAS_STORAGE_KEY, buildEquipamientoColumns } from './equipamientoColumns.js';
import { buildEquipamientoFilterFields } from './equipamientoFilterFields.js';
import { CAMPOS_DE_ORDEN, buildEquipamientoQuery } from './equipamientoGridQuery.js';

const queryVacia = (intl) => {
	const fields = buildEquipamientoFilterFields(intl);
	return serializeFilterState(fields, buildEmptyFilterState(fields));
};

const EquipamientoGrid = (props) => {
	const intl = useIntl();
	const columnas = useColumnVisibility(buildEquipamientoColumns(intl), EQUIPAMIENTO_COLUMNAS_STORAGE_KEY);
	const grid = useGridState({ campos: CAMPOS_DE_ORDEN, sortInicial: { campo: 'activo', direccion: 'asc' } });

	const [redirectTo, setRedirectTo] = useState(null);
	const [loading, setLoading] = useState(false);
	const [dropdownActive, setDropdownActive] = useState(false);
	const [rows, setRows] = useState([]);
	const [count, setCount] = useState(0);
	const [filterQuery, setFilterQuery] = useState(() => queryVacia(intl));
	const [searchActive, setSearchActive] = useState(false);
	const [filterResetInputs, setFilterResetInputs] = useState(false);
	const [exportAbierto, setExportAbierto] = useState(false);

	const getQuery = () => buildEquipamientoQuery(grid.pagination, filterQuery, grid.sortQuery);

	useEffect(() => {
		if (!Security.hasPermission('EQUIPAMIENTO_LISTAR')) {
			setRedirectTo('/error');
			return undefined;
		}
		let vigente = true;
		setLoading(true);
		Utils.getData('/equipamiento/newGrid' + getQuery())
			.then((data) => {
				if (!vigente) return;
				setRows((data && data.equipamientos) || []);
				setCount((data && data.pagination && data.pagination.count) || 0);
			})
			.catch(() => {})
			.finally(() => {
				if (vigente) setLoading(false);
			});

		return () => {
			vigente = false;
		};
	}, [grid.pagination, grid.sort, filterQuery]);

	const handleApplyFilters = (query) => {
		const vacia = queryVacia(intl);
		setSearchActive(Object.keys(query).some((k) => (query[k] || '') !== (vacia[k] || '')));
		grid.setPage(0);
		setFilterQuery(query);
	};

	const handleSearchClear = () => {
		grid.setPage(0);
		setFilterQuery(queryVacia(intl));
		setSearchActive(false);
		setFilterResetInputs(true);
	};

	const handleFilterReset = () => {
		grid.reset();
		columnas.mostrarTodas();
		setDropdownActive(false);
		handleSearchClear();
	};

	const columnasVisibles = () => 2 + buildEquipamientoColumns(intl).filter((c) => columnas.esVisible(c.name)).length;

	if (redirectTo) return <Redirect push to={redirectTo} />;

	return (
		<React.Fragment>
			<EquipamientoGridExport open={exportAbierto} handleClose={() => setExportAbierto(false)} queryFilters={getQuery} />
			<div className="card">
				<div className="card-content collpase show">
					<div className="card-body card-dashboard">
						<div className="container-fluid mb-1">
							<div className="row dt-icons">
								<div className="col">
									{Security.hasPermission('EQUIPAMIENTO_CREAR') && (
										<div
											className="btn btn-primary box-shadow-2 btn-round btn-lg btn-dt-main round-icon"
											style={{ height: 55 }}
											onClick={() => props.history.push(props.match.url + '/add')}
										>
											<i className="ft-plus"></i>
										</div>
									)}
									<div
										className="btn btn-grey-blue box-shadow-2 btn-round btn-lg btn-dt-main round-icon"
										onClick={() => setDropdownActive(!dropdownActive)}
									>
										{dropdownActive ? <UpIcon /> : <ExpandMoreIcon />}
									</div>
								</div>
								<div className="col">
									<GridSearchResultsBadge visible={searchActive} mostrados={rows.length} total={count} onReset={() => handleSearchClear()} />
								</div>
								<div className="col">
									<GridToolbar>
										<GridToolbarButton title={intl.formatMessage({ id: 'equipamientoGrid.tools.restaurar_filtros', defaultMessage: 'Restaurar filtros' })} onClick={() => handleFilterReset()}>
											<img id="filterReset" className="filter-reset" src={LimpiarFiltros} alt="" />
										</GridToolbarButton>
										<GridColumnSelector columns={columnas.seleccionables} esVisible={columnas.esVisible} onToggle={columnas.toggle} />
										<GridToolbarButton
											title={intl.formatMessage({ id: 'equipamientoGrid.tools.title_exportar.exportar_excel_title', defaultMessage: 'Exportar a Excel' })}
											icon="ft-upload"
											onClick={() => setExportAbierto(true)}
										/>
									</GridToolbar>
								</div>
							</div>
						</div>

						<EquipamientoGridFiltros
							open={dropdownActive}
							loading={loading}
							resetSignal={filterResetInputs}
							onResetSignalHandled={() => setFilterResetInputs(false)}
							onApply={handleApplyFilters}
						/>

						<TableContainer>
							<Table aria-label="equipamientos">
								<TableHead>
									<TableRow>
										<StyledTableCell style={{ width: 5 }} />
										<SortableCell field="identificador" sort={grid.sortQuery} onSort={grid.requestSort} show={columnas.esVisible('identificador')}>
											<FormattedMessage id="equipamientoGrid.columns_label.identificador" defaultMessage="Identificador" />
										</SortableCell>
										<SortableCell field="tipo" sort={grid.sortQuery} onSort={grid.requestSort} show={columnas.esVisible('tipo')}>
											<FormattedMessage id="equipamientoGrid.columns_label.tipo" defaultMessage="Tipo" />
										</SortableCell>
										<SortableCell field="marca" sort={grid.sortQuery} onSort={grid.requestSort} show={columnas.esVisible('marca')}>
											<FormattedMessage id="equipamientoGrid.columns_label.marca" defaultMessage="Marca" />
										</SortableCell>
										<SortableCell field="modelo" sort={grid.sortQuery} onSort={grid.requestSort} show={columnas.esVisible('modelo')}>
											<FormattedMessage id="equipamientoGrid.columns_label.modelo" defaultMessage="Modelo" />
										</SortableCell>
										<SortableCell field="vehiculo" sort={grid.sortQuery} onSort={grid.requestSort} show={columnas.esVisible('vehiculo')}>
											<FormattedMessage id="equipamientoGrid.columns_label.movil" defaultMessage="Móvil" />
										</SortableCell>
										<StyledTableCell />
									</TableRow>
								</TableHead>
								<TableBody>
									<GridLoadingRow visible={loading} columns={columnasVisibles()} />
									{!loading &&
										(rows.length > 0 ? (
											rows.map((equipamiento) => (
												<EquipamientoGridRow
													key={equipamiento.id}
													equipamiento={equipamiento}
													columnas={columnas}
													onVer={(fila) => props.history.push(props.match.url + '/' + fila.id)}
													onEditar={(fila) => props.history.push(props.match.url + '/' + fila.id + '/edit')}
												/>
											))
										) : (
											<GridEmptyRow columns={columnasVisibles()}>
												<FormattedMessage id="equipamientoGrid.sin_resultados" defaultMessage="No se encontraron equipamientos con los filtros seleccionados." />
											</GridEmptyRow>
										))}
								</TableBody>
							</Table>
						</TableContainer>

						<GridPagination
							count={count}
							page={grid.pagination.page}
							perPage={grid.pagination.perPage}
							onPageChange={(event, page) => grid.setPage(page)}
							onPerPageChange={(event) => grid.setPerPage(parseInt(event.target.value, 10))}
						/>
					</div>
				</div>
			</div>
		</React.Fragment>
	);
};

export default EquipamientoGrid;
